import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { OperationsService } from '@core/services';
import { ApiResponse } from '@core/types';
import {
  CommonArea,
  CommonAreaCreate,
  CommonAreaUpdate,
  CommonAreaSchedule,
  CommonAreaScheduleCreate,
  CommonAreaScheduleException,
  CommonAreaScheduleExceptionCreate,
  Reservation,
  ReservationCreate,
  ReservationFilter,
  MaintenanceTicket,
  MaintenanceTicketCreate,
  MaintenanceTicketUpdate,
  MaintenanceTicketFilter,
  InventoryItem,
  InventoryItemCreate,
  InventoryItemUpdate,
  InventoryItemFilter,
  MovingRequest,
  MovingRequestCreate,
  MovingRequestUpdate,
  MovingRequestFilter,
} from '@core/types/operations.type';
import { PaginationResult } from '@core/services/unit.service';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class OperationsQuery {
  private readonly service = inject(OperationsService);
  private readonly queryClient = inject(QueryClient);

  useCommonAreas() {
    return injectQuery(() => ({
      queryKey: QUERY_KEYS.commonAreas.list(),
      queryFn: () => lastValueFrom(this.service.getCommonAreas()),
      select: (res: ApiResponse<CommonArea[]>) => res.data || [],
      staleTime: environment.staleTime,
    }));
  }

  useCreateCommonArea() {
    return injectMutation(() => ({
      mutationFn: (data: CommonAreaCreate) => lastValueFrom(this.service.createCommonArea(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.commonAreas.all }),
    }));
  }

  useUpdateCommonArea() {
    return injectMutation(() => ({
      mutationFn: (data: CommonAreaUpdate) => lastValueFrom(this.service.updateCommonArea(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.commonAreas.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.commonAreas.all });
      },
    }));
  }

  useDeleteCommonArea() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.deleteCommonArea(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.commonAreas.all }),
    }));
  }

  useCommonAreaSchedules(commonAreaId: () => string | null) {
    return injectQuery(() => {
      const id = commonAreaId();
      return {
        queryKey: QUERY_KEYS.commonAreas.schedules(id || ''),
        queryFn: () => lastValueFrom(this.service.getCommonAreaSchedules(id!)),
        select: (res: ApiResponse<CommonAreaSchedule[]>) => res.data || [],
        enabled: !!id,
        staleTime: environment.staleTime,
      };
    });
  }

  useAddCommonAreaSchedule() {
    return injectMutation(() => ({
      mutationFn: (params: { commonAreaId: string; data: CommonAreaScheduleCreate }) =>
        lastValueFrom(this.service.addCommonAreaSchedule(params.commonAreaId, params.data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.commonAreas.schedules(variables.commonAreaId) });
      },
    }));
  }

  useDeleteCommonAreaSchedule() {
    return injectMutation(() => ({
      mutationFn: (params: { commonAreaId: string; scheduleId: string }) =>
        lastValueFrom(this.service.deleteCommonAreaSchedule(params.commonAreaId, params.scheduleId)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.commonAreas.schedules(variables.commonAreaId) });
      },
    }));
  }

  useCommonAreaExceptions(commonAreaId: () => string | null) {
    return injectQuery(() => {
      const id = commonAreaId();
      return {
        queryKey: QUERY_KEYS.commonAreas.exceptions(id || ''),
        queryFn: () => lastValueFrom(this.service.getCommonAreaExceptions(id!)),
        select: (res: ApiResponse<CommonAreaScheduleException[]>) => res.data || [],
        enabled: !!id,
        staleTime: environment.staleTime,
      };
    });
  }

  useAddCommonAreaException() {
    return injectMutation(() => ({
      mutationFn: (params: { commonAreaId: string; data: CommonAreaScheduleExceptionCreate }) =>
        lastValueFrom(this.service.addCommonAreaException(params.commonAreaId, params.data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.commonAreas.exceptions(variables.commonAreaId) });
      },
    }));
  }

  useDeleteCommonAreaException() {
    return injectMutation(() => ({
      mutationFn: (params: { commonAreaId: string; exceptionId: string }) =>
        lastValueFrom(this.service.deleteCommonAreaException(params.commonAreaId, params.exceptionId)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.commonAreas.exceptions(variables.commonAreaId) });
      },
    }));
  }

  useReservations(filter: () => ReservationFilter) {
    return injectQuery(() => {
      const f = filter();
      return {
        queryKey: QUERY_KEYS.reservations.list(f),
        queryFn: () => lastValueFrom(this.service.getReservations(f)),
        select: (res: ApiResponse<Reservation[]>) => res.data || [],
        staleTime: environment.staleTime,
      };
    });
  }

  useCreateReservation() {
    return injectMutation(() => ({
      mutationFn: (data: ReservationCreate) => lastValueFrom(this.service.createReservation(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reservations.all });
      },
    }));
  }

  useDeleteReservation() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.deleteReservation(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reservations.all });
      },
    }));
  }

  useMaintenanceTicketsGrid(config: () => {
    pageNumber: number;
    pageSize: number;
    searchTerm?: string;
    filter?: MaintenanceTicketFilter;
  }) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.maintenanceTickets.grid(c),
        queryFn: () => lastValueFrom(this.service.getMaintenanceTicketsGrid(c)),
        select: (res: ApiResponse<PaginationResult<MaintenanceTicket>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useMaintenanceTicket(id: () => string | null) {
    return injectQuery(() => {
      const ticketId = id();
      return {
        queryKey: QUERY_KEYS.maintenanceTickets.detail(ticketId || ''),
        queryFn: () => lastValueFrom(this.service.getMaintenanceTicket(ticketId!)),
        select: (res: ApiResponse<MaintenanceTicket>) => res.data,
        enabled: !!ticketId,
        staleTime: environment.staleTime,
      };
    });
  }

  useCreateMaintenanceTicket() {
    return injectMutation(() => ({
      mutationFn: (data: MaintenanceTicketCreate) =>
        lastValueFrom(this.service.createMaintenanceTicket(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.maintenanceTickets.all });
      },
    }));
  }

  useUpdateMaintenanceTicket() {
    return injectMutation(() => ({
      mutationFn: (data: MaintenanceTicketUpdate) =>
        lastValueFrom(this.service.updateMaintenanceTicket(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.maintenanceTickets.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.maintenanceTickets.all });
      },
    }));
  }

  useDeleteMaintenanceTicket() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.deleteMaintenanceTicket(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.maintenanceTickets.all });
      },
    }));
  }

  useInventoryItemsGrid(config: () => {
    pageNumber: number;
    pageSize: number;
    searchTerm?: string;
    filter?: InventoryItemFilter;
  }) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.inventoryItems.grid(c),
        queryFn: () => lastValueFrom(this.service.getInventoryItemsGrid(c)),
        select: (res: ApiResponse<PaginationResult<InventoryItem>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useInventoryItem(id: () => string | null) {
    return injectQuery(() => {
      const itemId = id();
      return {
        queryKey: QUERY_KEYS.inventoryItems.detail(itemId || ''),
        queryFn: () => lastValueFrom(this.service.getInventoryItem(itemId!)),
        select: (res: ApiResponse<InventoryItem>) => res.data,
        enabled: !!itemId,
        staleTime: environment.staleTime,
      };
    });
  }

  useCreateInventoryItem() {
    return injectMutation(() => ({
      mutationFn: (data: InventoryItemCreate) =>
        lastValueFrom(this.service.createInventoryItem(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inventoryItems.all });
      },
    }));
  }

  useUpdateInventoryItem() {
    return injectMutation(() => ({
      mutationFn: (data: InventoryItemUpdate) =>
        lastValueFrom(this.service.updateInventoryItem(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inventoryItems.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inventoryItems.all });
      },
    }));
  }

  useDeleteInventoryItem() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.deleteInventoryItem(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inventoryItems.all });
      },
    }));
  }

  useMovingRequestsGrid(config: () => {
    pageNumber: number;
    pageSize: number;
    searchTerm?: string;
    filter?: MovingRequestFilter;
  }) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.movingRequests.grid(c),
        queryFn: () => lastValueFrom(this.service.getMovingRequestsGrid(c)),
        select: (res: ApiResponse<PaginationResult<MovingRequest>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useMovingRequest(id: () => string | null) {
    return injectQuery(() => {
      const requestId = id();
      return {
        queryKey: QUERY_KEYS.movingRequests.detail(requestId || ''),
        queryFn: () => lastValueFrom(this.service.getMovingRequest(requestId!)),
        select: (res: ApiResponse<MovingRequest>) => res.data,
        enabled: !!requestId,
        staleTime: environment.staleTime,
      };
    });
  }

  useCreateMovingRequest() {
    return injectMutation(() => ({
      mutationFn: (data: MovingRequestCreate) =>
        lastValueFrom(this.service.createMovingRequest(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.movingRequests.all });
      },
    }));
  }

  useUpdateMovingRequest() {
    return injectMutation(() => ({
      mutationFn: (data: MovingRequestUpdate) =>
        lastValueFrom(this.service.updateMovingRequest(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.movingRequests.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.movingRequests.all });
      },
    }));
  }

  useDeleteMovingRequest() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.deleteMovingRequest(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.movingRequests.all });
      },
    }));
  }
}

